import { apiFetch } from "../../../shared/api-client";
import type { CompanySettings } from "./company-settings-api";
import type { JobsheetSections } from "./job-sheet-templates-api";
import type { InvoiceSections, QuoteSections } from "./quote-templates-api";

export type DocumentPreviewKind = "quote" | "invoice" | "jobsheet";

export interface DocumentPreviewRequest {
  kind: DocumentPreviewKind;
  /**
   * "html" returns the editable HTML twin the settings studios render;
   * omitted/"pdf" returns the production PDF. Settings previews always
   * request "html".
   */
  format?: "pdf" | "html";
  company_name: string;
  contact_email: string;
  public_url: string;
  payment_account_name: string;
  payment_bank_name: string;
  payment_account_number: string;
  gst_number: string;
  nzbn: string;
  terms_url: string;
  payment_terms_text: string;
  extra_notes_text: string;
  contractor_terms_url: string;
  deposit_percent_default: number;
  quote_valid_days_default: number;
  multi_day_extra_day_percent: number;
  /** Jobsheet previews only: the template's toggles + name (#188). */
  jobsheet_sections?: JobsheetSections;
  jobsheet_template_name?: string;
  /** Quote previews only: the quote template's toggles + name. */
  quote_sections?: QuoteSections;
  quote_template_name?: string;
  /** Invoice previews only: the template's invoice-face toggles and which
   * face to preview ("deposit" default; view-only — both share the set). */
  invoice_sections?: InvoiceSections;
  invoice_face?: "deposit" | "balance";
}

export interface DocumentPreviewResponse {
  media_type: "text/html" | "application/pdf";
  filename?: string;
  subject?: string;
  html?: string;
  pdf_base64?: string;
}

export function documentPreviewRequest(
  settings: CompanySettings,
  kind: DocumentPreviewKind,
  overrides: Partial<Omit<DocumentPreviewRequest, "kind">> = {},
): DocumentPreviewRequest {
  return {
    kind,
    company_name: settings.name,
    contact_email: settings.contact_email,
    public_url: settings.public_url,
    payment_account_name: settings.payment_account_name,
    payment_bank_name: settings.payment_bank_name,
    payment_account_number: settings.payment_account_number,
    gst_number: settings.gst_number,
    nzbn: settings.nzbn,
    terms_url: settings.terms_url,
    payment_terms_text: settings.payment_terms_text,
    extra_notes_text: settings.extra_notes_text,
    contractor_terms_url: settings.contractor_terms_url,
    deposit_percent_default: settings.deposit_percent_default,
    quote_valid_days_default: settings.quote_valid_days_default,
    multi_day_extra_day_percent: settings.multi_day_extra_day_percent,
    ...overrides,
  };
}

/**
 * One preview call: the wire body plus the caller's cancellation signal. The
 * signal rides on the call object rather than a second argument so a studio
 * builds and hands over exactly one thing; without it a superseded keystroke
 * preview ran to completion server-side and could land after the request that
 * replaced it.
 */
export type DocumentPreviewCall = DocumentPreviewRequest & {
  signal?: AbortSignal;
};

export async function getDocumentPreview(
  call: DocumentPreviewCall,
): Promise<DocumentPreviewResponse> {
  const { signal, ...body } = call;
  return apiFetch<DocumentPreviewResponse>("/api/admin/document-preview", {
    method: "POST",
    body,
    signal,
  });
}
